import axiosInstance from './AxiosInstance'
import { type ListUsersParams, type ListUsersResponse, type RoleName, type InvitationView, type ListInvitationsParams, type ListInvitationsResponse, type RevokeInvitationResult, type ListMyClientsResponse, type ClientDetail, type MenstruationMonthView, type ClientChartBundle, type KnowledgeSummary, type IngestKnowledgeRequest, type IngestKnowledgeResponse, type ListKnowledgeSourcesResponse, type DeleteKnowledgeSourceResponse, type ChatCategory, type KnowledgeDocument, type CreateKnowledgeDocumentRequest, type UpdateKnowledgeDocumentRequest, type AddKnowledgeContentResponse, type Organisatie, type CreateOrgRequest, type CreateOrgResponse, type PlatformFeatures } from './models'

export const joinInvitationApi = (values: any) => {
  return axiosInstance
    .post('/join-invitation', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const getInvitaionApi = (token: any) => {
  return axiosInstance
    .get(`/invited-users/${token}`)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const inviteApi = (values: any) => {
  return axiosInstance
    .post('/user-invitiation', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const registerApi = (values: any) => {
  return axiosInstance
    .post('/register', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const loginApi = (values: any) => {
  return axiosInstance
    .post('/login', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

// ── 2FA TOTP (Tiket D) ──────────────────────────────────────────────────
// During the login challenge the user has no session yet, so these calls
// carry the short-lived STEP-UP token explicitly. For self-service enrol
// (already logged in) the token is omitted and AxiosInstance's interceptor
// attaches the access token instead.
const stepUpConfig = (stepUpToken?: string) =>
  stepUpToken
    ? { headers: { Authorization: `Bearer ${stepUpToken}` } }
    : undefined

export const totpVerifyApi = (code: string, stepUpToken: string) =>
  axiosInstance
    .post('/v1/auth/2fa:verify', { code }, stepUpConfig(stepUpToken))
    .then((response) => response.data)
    .catch((error) => {
      throw error
    })

export const totpEnrollApi = (stepUpToken?: string) =>
  axiosInstance
    .post('/v1/auth/2fa/totp:enroll', {}, stepUpConfig(stepUpToken))
    .then((response) => response.data)
    .catch((error) => {
      throw error
    })

export const totpConfirmApi = (code: string, stepUpToken?: string) =>
  axiosInstance
    .post('/v1/auth/2fa/totp:confirm', { code }, stepUpConfig(stepUpToken))
    .then((response) => response.data)
    .catch((error) => {
      throw error
    })

export const adminResetTotpApi = (userId: string) =>
  axiosInstance
    .post(`/v1/admin/users/${userId}/totp:reset`)
    .then((response) => response.data)
    .catch((error) => {
      throw error
    })

// US-02: self-service 2FA reset for a locked-out superbeheerder. Request
// emails a one-time link; confirm redeems its token and wipes 2FA. Public
// endpoints — the email/link possession is the factor.
export const request2faResetApi = (email: string) =>
  axiosInstance
    .post('/v1/auth/2fa/reset:request', { email })
    .then((response) => response.data)
    .catch((error) => {
      throw error
    })

export const confirm2faResetApi = (token: string) =>
  axiosInstance
    .post('/v1/auth/2fa/reset:confirm', { token })
    .then((response) => response.data)
    .catch((error) => {
      throw error
    })

export const requestOTPApi = (values: any) => {
  return axiosInstance
    .post('/request-otp', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const validateOTPApi = (values: any) => {
  return axiosInstance
    .post('/verify-otp', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const resetPasswordApi = (values: any) => {
  return axiosInstance
    .post('/reset-password', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const changePasswordApi = (values: any) => {
  return axiosInstance
    .post('/change-password', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const verifyEmailApi = (values: any) => {
  return axiosInstance
    .get(`/register/verifaction-mail/${values}`)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const acceptAgreementApi = () => {
  return axiosInstance
    .post('/accept-agreement', {
      privacy_policy: 'true',
      term_of_use: 'true',
      personal_data: 'true',
    })
    .then((response) => {
      return response
    })
    .catch((error) => {
      throw error
    })
}

export const myAccessLogApi = () => {
  return axiosInstance
    .get(`/get-my-log-activity`)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

/**
 * Merged delivery-log inbox (WhatsApp + Email). Powers the bell icon in
 * the global header. Cursor-paginated under the hood but the header only
 * shows the most recent page (default 50).
 *
 * Response shape: `{items: NotificationView[], next_cursor: string|null}`
 * See `apps/api/crates/bloom-auth/src/notifications.rs`.
 */
export const accountNotificationsApi = (params?: {
  limit?: number
  cursor?: string
  channel?: 'all' | 'whatsapp' | 'email'
}) => {
  const qs = new URLSearchParams()
  if (params?.limit) qs.set('limit', String(params.limit))
  if (params?.cursor) qs.set('cursor', params.cursor)
  if (params?.channel && params.channel !== 'all') qs.set('channel', params.channel)
  const suffix = qs.toString() ? `?${qs.toString()}` : ''
  return axiosInstance
    .get(`/v1/account/notifications${suffix}`)
    .then((response) => response.data)
    .catch((error) => {
      throw error
    })
}

export const accountUpdateApi = (values: any) => {
  return axiosInstance
    .put('/account-update', values)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

// ── GDPR data portability (US-6.1) ─────────────────────────────────────────
// Downloads the caller's full personal-data export (GDPR art. 20). The BE
// streams JSON with a `Content-Disposition: attachment` header, but because the
// request is XHR-authed (Bearer token) the browser can't navigate to it and act
// on that header — so we pull the payload as a blob and let the caller trigger a
// client-side download.
export const exportMyDataApi = () => {
  return axiosInstance
    .get('/v1/account/me:export', { responseType: 'blob' })
    .then((response) => {
      return response.data as Blob
    })
    .catch((error) => {
      throw error
    })
}

export const listUsersApi = (params: ListUsersParams = {}) => {
  const search: Record<string, string> = {}
  if (params.q && params.q.length > 0) search.q = params.q
  if (params.role) search.role = params.role
  if (typeof params.suspended === 'boolean') search.suspended = String(params.suspended)
  if (params.limit) search.limit = String(params.limit)
  if (typeof params.offset === 'number') search.offset = String(params.offset)
  if (params.membership) search.membership = params.membership
  return axiosInstance
    .get<ListUsersResponse>('/v1/admin/users', { params: search })
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

// ── Organisaties (multi-tenant, Tiket E4) — superbeheerder only ────────────
export const listOrganisatiesApi = () => {
  return axiosInstance
    .get<Organisatie[]>('/v1/admin/organizations')
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const createOrganisatieApi = (body: CreateOrgRequest) => {
  return axiosInstance
    .post<CreateOrgResponse>('/v1/admin/organizations', body)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const renameOrganisatieApi = (id: string, name: string) => {
  return axiosInstance
    .patch(`/v1/admin/organizations/${id}`, { name })
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const setOrganisatieSuspendedApi = (id: string, suspended: boolean) => {
  const action = suspended ? 'suspend' : 'unsuspend'
  return axiosInstance
    .post(`/v1/admin/organizations/${id}/${action}`)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const changeRoleApi = (userId: string, role: RoleName) => {
  return axiosInstance
    .patch(`/v1/admin/users/${userId}/role`, { role })
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const suspendUserApi = (userId: string) => {
  return axiosInstance
    .post(`/v1/admin/users/${userId}/suspend`)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

export const unsuspendUserApi = (userId: string) => {
  return axiosInstance
    .post(`/v1/admin/users/${userId}/unsuspend`)
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

// US-6.1 D5 (AC-4): end a client's care relationship with this organisation.
// The whole org loses access to the client's data; the data itself is retained.
export const endClientCareApi = (clientId: string, reason?: string) => {
  return axiosInstance
    .post(`/v1/admin/clients/${clientId}/care:end`, { reason })
    .then((response) => {
      return response.data
    })
    .catch((error) => {
      throw error
    })
}

// ── behandelaar (supervisor) client oversight ──

export const listMyClientsApi = (
  params: { q?: string; limit?: number; offset?: number } = {}
) => {
  const search: Record<string, string> = {}
  if (params.q && params.q.length > 0) search.q = params.q
  if (params.limit) search.limit = String(params.limit)
  if (typeof params.offset === 'number') search.offset = String(params.offset)
  return axiosInstance
    .get<ListMyClientsResponse>('/v1/supervisor/clients', { params: search })
    .then((response) => response.data)
}

export const getMyClientApi = (id: string) =>
  axiosInstance
    .get<ClientDetail>(`/v1/supervisor/clients/${id}`)
    .then((response) => response.data)

// Read-only kalender + wellness for an assigned client (Module 9 Slice 2).
export const getMyClientMenstruationApi = (
  id: string,
  month: number,
  year: number
) =>
  axiosInstance
    .get<MenstruationMonthView>(`/v1/supervisor/clients/${id}/menstruation`, {
      params: { month, year },
    })
    .then((response) => response.data)

export const getMyClientChartApi = (
  id: string,
  month: number,
  year: number
) =>
  axiosInstance
    .get<ClientChartBundle>(`/v1/supervisor/clients/${id}/feelings/chart`, {
      params: { month, year },
    })
    .then((response) => response.data)

export const listInvitationsApi = (params: ListInvitationsParams = {}) =>
  axiosInstance
    .get<ListInvitationsResponse>('/v1/admin/invitations', { params })
    .then((r) => r.data)

// FB-7: praktisi (behandelaar) self-scoped invite status — only the
// invites the caller issued. Same response shape as the admin list, but
// gated by SupervisorAuthUser and filtered to invited_by = caller on the BE.
export const listMyInvitationsApi = (params: ListInvitationsParams = {}) =>
  axiosInstance
    .get<ListInvitationsResponse>('/v1/supervisor/invitations', { params })
    .then((r) => r.data)

export const resendInvitationApi = (id: string) =>
  axiosInstance
    .post<InvitationView>(`/v1/admin/invitations/${id}/resend`)
    .then((r) => r.data)

export const revokeInvitationApi = (id: string) =>
  axiosInstance
    .post<RevokeInvitationResult>(`/v1/admin/invitations/${id}/revoke`)
    .then((r) => r.data)

// ── knowledge base / RAG content upload (Tiket B) ──

export const getKnowledgeSummaryApi = () =>
  axiosInstance
    .get<KnowledgeSummary>('/v1/admin/knowledge')
    .then((r) => r.data)

export const postKnowledgeApi = (body: IngestKnowledgeRequest) =>
  axiosInstance
    .post<IngestKnowledgeResponse>('/v1/admin/knowledge', body)
    .then((r) => r.data)

export const listKnowledgeSourcesApi = () =>
  axiosInstance
    .get<ListKnowledgeSourcesResponse>('/v1/admin/knowledge/sources')
    .then((r) => r.data)

export const deleteKnowledgeSourceApi = (
  title: string,
  source_url: string | null
) =>
  axiosInstance
    .delete<DeleteKnowledgeSourceResponse>('/v1/admin/knowledge/sources', {
      data: { title, source_url },
    })
    .then((r) => r.data)

// ── knowledge documents: books, courses, articles (23-9-2026) ──

export const listChatCategoriesApi = () =>
  axiosInstance
    .get<{ items: ChatCategory[] }>('/v1/chat/categories')
    .then((r) => r.data.items)

export const listKnowledgeDocumentsApi = () =>
  axiosInstance
    .get<{ items: KnowledgeDocument[] }>('/v1/admin/knowledge/documents')
    .then((r) => r.data.items)

export const createKnowledgeDocumentApi = (body: CreateKnowledgeDocumentRequest) =>
  axiosInstance
    .post<KnowledgeDocument>('/v1/admin/knowledge/documents', body)
    .then((r) => r.data)

export const updateKnowledgeDocumentApi = (
  id: string,
  body: UpdateKnowledgeDocumentRequest
) =>
  axiosInstance
    .patch<KnowledgeDocument>(`/v1/admin/knowledge/documents/${id}`, body)
    .then((r) => r.data)

export const deleteKnowledgeDocumentApi = (id: string) =>
  axiosInstance
    .delete(`/v1/admin/knowledge/documents/${id}`)
    .then(() => undefined)

/** Text for a document; a book is sent in parts. */
export const addKnowledgeContentApi = (
  id: string,
  content: string,
  section?: string
) =>
  axiosInstance
    .post<AddKnowledgeContentResponse>(
      `/v1/admin/knowledge/documents/${id}/content`,
      { content, section }
    )
    .then((r) => r.data)

/** A .docx or .epub file, read on the server. */
export const uploadKnowledgeFileApi = (id: string, file: File) =>
  axiosInstance
    .post<AddKnowledgeContentResponse>(
      `/v1/admin/knowledge/documents/${id}/file`,
      file,
      {
        params: { file_name: file.name },
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      }
    )
    .then((r) => r.data)

// ── Platform feature flags (CHL-065-009) ───────────────────────────────────
/** Readable by every admin scope; the console hides features that are off. */
export const getFeaturesApi = () =>
  axiosInstance
    .get<PlatformFeatures>('/v1/admin/features')
    .then((r) => r.data)

/** Superbeheerder-only switch. Flipping it never deletes anything. */
export const setFeaturesApi = (body: PlatformFeatures) =>
  axiosInstance
    .patch<PlatformFeatures>('/v1/admin/features', body)
    .then((r) => r.data)
